/**
 * TypeScript types for Workflow Replay feature.
 */

/**
 * Summary of a workflow run for the run selector.
 */
export interface WorkflowRunSummary {
    id: string;
    status: 'pending' | 'running' | 'waiting' | 'parallel' | 'mapping' | 'awaiting_child' | 'completed' | 'failed' | 'cancelled' | 'timed_out';
    created: string;
    modified: string;
    trace_id: string | null;
    root_span_id: string | null;
    has_trace: boolean;
    input_summary?: string | null;
    duration_ms?: number | null;
    current_step_id?: string | null;
}

/**
 * Checkpoint data for a workflow state execution.
 */
export interface CheckpointData {
    id: string;
    state_name: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    attempt: number;
    span_id: string | null;
    duration_ms: number | null;
    input_data: Record<string, unknown>;
    result: Record<string, unknown> | null;
    context_before: Record<string, unknown>;
    context_after: Record<string, unknown> | null;
    /** 
     * Only the state variables that were changed/written by this step.
     * null if no granular variable tracking is available (legacy checkpoints).
     */
    context_changes: Record<string, unknown> | null;
    error: {
        type?: string;
        message?: string;
        code?: number;
        trace?: unknown[];
    } | null;
    created: string;
}

/**
 * Aggregated checkpoint data for nodes inside map iterations.
 * Since a Map state runs N iterations, each inner node may have N checkpoints.
 */
export interface AggregatedCheckpointData {
    /** Simple state name (e.g., "ExtractDocumentInfo") */
    stateName: string;
    /** Map node ID containing this state (e.g., "ExtractDocuments") */
    mapNodeId: string;
    /** Total number of iterations */
    totalIterations: number;
    /** Count by status */
    completedCount: number;
    failedCount: number;
    runningCount: number;
    pendingCount: number;
    /** Total duration across all iterations (ms) */
    totalDurationMs: number;
    /** Average duration per iteration (ms) */
    avgDurationMs: number;
    /** Individual iteration checkpoints, indexed by iteration number */
    iterations: Map<number, CheckpointData>;
    /** The latest checkpoint (for showing most recent error, etc.) */
    latestCheckpoint: CheckpointData | null;
    /** Whether any iteration failed */
    hasFailed: boolean;
    /** Whether all iterations completed */
    allCompleted: boolean;
    /** Aggregated status for display */
    aggregatedStatus: 'completed' | 'failed' | 'running' | 'pending' | 'partial';
}

/**
 * Result of looking up a checkpoint for a node.
 * Can be a single checkpoint (for non-map nodes) or aggregated data (for map iteration nodes).
 */
export interface CheckpointLookupResult {
    /** Whether this is for a node inside a map iterator */
    isMapIteration: boolean;
    /** Whether this is for a node inside a parallel branch */
    isParallelBranch: boolean;
    /** Single checkpoint (for non-map nodes) */
    checkpoint: CheckpointData | null;
    /** Aggregated data (for map iteration nodes) */
    aggregated: AggregatedCheckpointData | null;
}

/**
 * Log entry from Loki.
 */
export interface LogEntry {
    timestamp: string;
    level: 'debug' | 'info' | 'warning' | 'error';
    message: string;
    attributes: Record<string, unknown>;
    span_id?: string | null;
    trace_id?: string | null;
}

/**
 * State for the replay mode.
 */
export interface ReplayState {
    isReplayMode: boolean;
    selectedRunId: string | null;
    selectedRun: WorkflowRunSummary | null;
    checkpoints: Map<string, CheckpointData>;
    logs: LogEntry[];
    selectedNodeId: string | null;
    isLoading: boolean;
    error: string | null;
    grafanaUrl: string | null;
}

/**
 * API response for listing workflow runs.
 */
export interface ListRunsResponse {
    data: WorkflowRunSummary[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

/**
 * API response for getting checkpoints.
 */
export interface GetCheckpointsResponse {
    data: CheckpointData[];
    run: {
        id: string;
        status: string;
        trace_id: string | null;
        root_span_id: string | null;
        created: string;
        grafana_url: string | null;
    };
}

/**
 * API response for getting logs.
 */
export interface GetLogsResponse {
    data: LogEntry[];
    trace_id?: string;
    grafana_url?: string | null;
    message?: string;
}

/**
 * Child workflow run summary (for sub-workflow navigation).
 */
export interface ChildWorkflowRunSummary {
    id: string;
    capability_id: string;
    workflow_key: string | null;
    workflow_name: string | null;
    status: WorkflowRunSummary['status'];
    created: string;
    modified: string;
    trace_id: string | null;
    has_trace: boolean;
    duration_ms: number | null;
    parent_step_id: string | null;
    parent_state_name: string | null;
}

/**
 * API response for getting child workflow runs.
 */
export interface GetChildRunsResponse {
    data: ChildWorkflowRunSummary[];
    parent_run_id: string;
}

/**
 * Replay context for navigating into sub-workflows.
 * This is passed along when drilling down into a child workflow.
 */
export interface ReplayContext {
    /** ID of the parent run that invoked this workflow */
    parentRunId: string;
    /** Step ID that invoked this child workflow */
    parentStepId: string;
    /** State name of the parent node */
    parentStateName: string;
    /** The child run to display */
    childRunId: string;
    /** Full navigation path of replay contexts (for deep nesting) */
    ancestors: Array<{
        runId: string;
        workflowKey: string;
        workflowName: string;
    }>;
}

/**
 * Status badge variant based on run/checkpoint status.
 */
export function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
    switch (status) {
        case 'completed':
            return 'default';
        case 'running':
        case 'pending':
        case 'waiting':
        case 'parallel':
        case 'mapping':
        case 'awaiting_child':
            return 'secondary';
        case 'failed':
        case 'timed_out':
        case 'cancelled':
            return 'destructive';
        default:
            return 'outline';
    }
}

/**
 * Format duration in milliseconds to human-readable string.
 */
export function formatDuration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) {
        return '-';
    }
    if (ms < 1000) {
        return `${ms}ms`;
    }
    if (ms < 60000) {
        return `${(ms / 1000).toFixed(1)}s`;
    }
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.round((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
}

/**
 * Get color class for log level.
 */
export function getLogLevelColor(level: string): string {
    switch (level.toLowerCase()) {
        case 'error':
            return 'text-red-600 bg-red-50';
        case 'warning':
        case 'warn':
            return 'text-amber-600 bg-amber-50';
        case 'debug':
            return 'text-slate-500 bg-slate-50';
        case 'info':
        default:
            return 'text-blue-600 bg-blue-50';
    }
}

